import { TableRow, Typography, styled } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { grey } from '@mui/material/colors';
import { TableCell } from 'further-ui/components/Table';
import { numberToCurrencyString } from 'further-ui/utils';
import { CurrencyCode } from 'further-types/lp/vehicle';
import type { RowData } from './types';

export type { RowData };

const SectionHeaderTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[900],
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.h6.fontSize,
  fontStyle: 'normal',
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: theme.typography.h6.lineHeight,
}));

const SectionHeaderRow = styled(TableRow)(({ theme }) => ({
  height: theme.spacing(13.5),
  '& > .MuiTableCell-root': {
    height: theme.spacing(13.5),
    minHeight: theme.spacing(13.5),
    maxHeight: theme.spacing(13.5),
    boxSizing: 'border-box',
    verticalAlign: 'middle',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
}));

const TotalValueCell = styled(TableCell)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold,
}));

const DividerRightCell = styled(TableCell)(({ theme }) => ({
  borderRight: `1px solid ${theme.palette.grey[300]}`,
}));

const LabelCell = styled(DividerRightCell)(() => ({
  verticalAlign: 'top',
}));

const ContentLabelCell = styled(LabelCell)(() => ({}));

const LabelSubtitlePair = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'baseline',
  flexWrap: 'wrap',
  columnGap: theme.spacing(2),
}));

const IndentedLabelSubtitlePair = styled(LabelSubtitlePair)(({ theme }) => ({
  marginLeft: theme.spacing(1),
}));

const LpSinceInceptionCell = styled(TableCell)(({ theme }) => ({
  borderRight: `1px solid ${theme.palette.grey[300]}`,
}));

const TotalLpSinceInceptionCell = styled(TotalValueCell)(({ theme }) => ({
  borderRight: `1px solid ${theme.palette.grey[300]}`,
}));

const SubtitleInline = styled('span')(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
}));

const DataRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== 'rowBackgroundColor',
})<{ rowBackgroundColor?: string }>(({ rowBackgroundColor, theme }) => ({
  height: theme.spacing(10),
  '& > .MuiTableCell-root': {
    height: theme.spacing(10),
    minHeight: theme.spacing(10),
    maxHeight: theme.spacing(10),
    boxSizing: 'border-box',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    ...(rowBackgroundColor ? { backgroundColor: rowBackgroundColor } : {}),
  },
}));

const PcapDataRow: React.FC<{ row: RowData; currency: CurrencyCode }> = ({
  row,
  currency,
}) => {
  const theme = useTheme();
  const formatValue = (value: number) =>
    numberToCurrencyString(value, {
      currency,
      negativeInBrackets: true,
    });
  const valueTypographyProps = row.isTotal
    ? { fontWeight: theme.typography.fontWeightBold }
    : {};

  if (row.isSectionHeader) {
    return (
      <SectionHeaderRow>
        <DividerRightCell size="medium">
          <SectionHeaderTypography variant="h6">
            {row.label}
          </SectionHeaderTypography>
        </DividerRightCell>
        <TableCell size="medium" />
        <TableCell size="medium" />
        <LpSinceInceptionCell size="medium" />
        <TableCell size="medium" />
        <TableCell size="medium" />
        <TableCell size="medium" />
      </SectionHeaderRow>
    );
  }

  const ValueCell = row.isTotal ? TotalValueCell : TableCell;

  const labelContent = row.isTotal ? (
    <LabelSubtitlePair>
      <Typography component="span" variant="h6" fontWeight={700}>
        {row.label}
      </Typography>
      {row.subtitle && <SubtitleInline>{row.subtitle}</SubtitleInline>}
    </LabelSubtitlePair>
  ) : (
    <IndentedLabelSubtitlePair>
      <Typography component="span" variant="h6">
        {row.label}
      </Typography>
      {row.subtitle && <SubtitleInline>{row.subtitle}</SubtitleInline>}
    </IndentedLabelSubtitlePair>
  );

  const rowBackgroundColor = row.isTotal
    ? grey[100]
    : row.isSubtleBackground
    ? grey[50]
    : undefined;

  const RowLabelCell = row.isTotal ? LabelCell : ContentLabelCell;

  return (
    <DataRow rowBackgroundColor={rowBackgroundColor}>
      <RowLabelCell size="medium">{labelContent}</RowLabelCell>
      <ValueCell align="right" size="medium">
        <Typography variant="h6" component="span" {...valueTypographyProps}>
          {formatValue(row.lpQtd)}
        </Typography>
      </ValueCell>
      <ValueCell align="right" size="medium">
        <Typography variant="h6" component="span" {...valueTypographyProps}>
          {formatValue(row.lpYtd)}
        </Typography>
      </ValueCell>
      {row.isTotal ? (
        <TotalLpSinceInceptionCell align="right" size="medium">
          <Typography variant="h6" component="span" {...valueTypographyProps}>
            {formatValue(row.lpSi)}
          </Typography>
        </TotalLpSinceInceptionCell>
      ) : (
        <LpSinceInceptionCell align="right" size="medium">
          <Typography variant="h6" component="span">
            {formatValue(row.lpSi)}
          </Typography>
        </LpSinceInceptionCell>
      )}
      <ValueCell align="right" size="medium">
        <Typography variant="h6" component="span" {...valueTypographyProps}>
          {formatValue(row.fundQtd)}
        </Typography>
      </ValueCell>
      <ValueCell align="right" size="medium">
        <Typography variant="h6" component="span" {...valueTypographyProps}>
          {formatValue(row.fundYtd)}
        </Typography>
      </ValueCell>
      <ValueCell align="right" size="medium">
        <Typography variant="h6" component="span" {...valueTypographyProps}>
          {formatValue(row.fundSi)}
        </Typography>
      </ValueCell>
    </DataRow>
  );
};

export default PcapDataRow;
